import React, { useCallback } from "react";
import { LinePath, Line, AreaClosed } from "@visx/shape";
import { curveMonotoneX } from "@visx/curve";
import { Group } from "@visx/group";
import { GridRows } from "@visx/grid";
import { AxisBottom } from "@visx/axis";
import { LinearGradient } from "@visx/gradient";
import {
  useTooltip,
  useTooltipInPortal,
  defaultStyles as defaultTooltipStyles,
} from "@visx/tooltip";
import { localPoint } from "@visx/event";
import {
  DataPoint,
  FormattedDataPoint,
  TimeSeriesChartProps,
  TooltipData,
} from "./TimeSeriesChart.types";
import { bisectDate, useTickConfig } from "./TimeSeriesChart.helpers";
import { useTimeSeriesChart } from "./TimeSeriesChart.hooks";
import { formatNumber } from "../../utils/formatUtils";

const TimeSeriesChart = <T extends DataPoint>(
  props: TimeSeriesChartProps<T>
) => {
  const {
    rootRef,
    rootStyle,
    innerHeight,
    innerWidth,
    formattedData,
    xScale,
    yScale,
    margin,
    height,
    width,
    showArea,
    showGridRows,
    showPoints,
    numYTicks,
    chartTheme,
    tooltipStyles,
  } = useTimeSeriesChart(props);

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<TooltipData>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
    debounce: 100,
  });

  const getTickConfig = useTickConfig(formattedData, xScale);

  const handleTooltip = useCallback(
    (
      event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>
    ) => {
      if (!formattedData || formattedData.length === 0) return;

      const { x: eventX } = localPoint(event) || { x: 0 };
      const pointerX = eventX - margin.left;

      const xDate = xScale.invert(pointerX);
      const index = bisectDate(formattedData, xDate, 1);
      const d0 = formattedData[index - 1];
      const d1 = formattedData[index];

      let closestPoint = d0;
      if (d0 && d1) {
        closestPoint =
          xDate.getTime() - d0.date.getTime() >
          d1.date.getTime() - xDate.getTime()
            ? d1
            : d0;
      } else if (d1) {
        closestPoint = d1;
      }

      if (closestPoint) {
        showTooltip({
          tooltipData: {
            point: closestPoint,
            x: xScale(closestPoint.date),
            y: yScale(closestPoint.value),
          },
          tooltipLeft: xScale(closestPoint.date) + margin.left,
          tooltipTop: yScale(closestPoint.value) + margin.top,
        });
      }
    },
    [xScale, yScale, formattedData, margin.left, margin.top, showTooltip]
  );

  if (width <= 0 || height <= 0) {
    return <div style={{ ...rootStyle }} ref={rootRef}></div>;
  }

  const areaGradientId = `area-gradient-${Math.random()
    .toString(36)
    .substring(7)}`;

  return (
    <div
      style={{ position: "relative", ...rootStyle }}
      ref={(el) => {
        // Assign to both refs
        if (containerRef && typeof containerRef === "function") {
          containerRef(el);
        }
        if (rootRef && typeof rootRef === "function") {
          rootRef(el);
        }
      }}
    >
      <svg width={width} height={height}>
        {/* Background */}
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill={chartTheme.background}
        />

        {/* Define gradients */}
        <LinearGradient
          id={areaGradientId}
          from={chartTheme.gradient.from}
          to={chartTheme.gradient.to}
          fromOpacity={chartTheme.gradient.fromOpacity}
          toOpacity={chartTheme.gradient.toOpacity}
          vertical={true}
        />

        {/* Chart Area Group */}
        <Group left={margin.left} top={margin.top}>
          {/* Chart area background */}
          <rect
            x={0}
            y={0}
            width={innerWidth}
            height={innerHeight}
            fill={
              formattedData.length === 0 ? "#FFFFFF" : chartTheme.background
            }
            rx={0}
          />

          {/* Grid lines */}
          {showGridRows && (
            <GridRows
              scale={yScale}
              width={innerWidth}
              stroke={chartTheme.grid}
              numTicks={numYTicks}
              pointerEvents="none"
            />
          )}

          {/* Area under the curve */}
          {showArea && formattedData.length > 0 && (
            <AreaClosed<FormattedDataPoint>
              data={formattedData}
              x={(d) => xScale(d.date)}
              y={(d) => yScale(d.value)}
              yScale={yScale}
              fill={`url(#${areaGradientId})`}
              curve={curveMonotoneX}
              pointerEvents="none"
            />
          )}

          {/* Line path */}
          {formattedData.length > 0 && (
            <LinePath<FormattedDataPoint>
              data={formattedData}
              x={(d) => xScale(d.date)}
              y={(d) => yScale(d.value)}
              stroke={chartTheme.line.color}
              strokeWidth={chartTheme.line.width}
              curve={curveMonotoneX}
              pointerEvents="none"
            />
          )}

          {/* Data points */}
          {showPoints &&
            formattedData.map((d, i) => (
              <circle
                key={`point-${i}`}
                cx={xScale(d.date)}
                cy={yScale(d.value)}
                r={chartTheme.point.radius}
                fill={chartTheme.point.color}
                pointerEvents="none"
              />
            ))}

          {/* Tooltip Interaction Layer */}
          <rect
            x={0}
            y={0}
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onTouchStart={handleTooltip}
            onTouchMove={handleTooltip}
            onMouseMove={handleTooltip}
            onMouseLeave={hideTooltip}
          />

          {/* Vertical line at the hovered point */}
          {tooltipOpen && tooltipData && (
            <Line
              from={{ x: tooltipData.x, y: 0 }}
              to={{ x: tooltipData.x, y: innerHeight }}
              stroke={"#131B39"}
              strokeWidth={1}
              style={{ pointerEvents: "none" }}
            />
          )}

          {/* Circle marker for hovered point */}
          {tooltipOpen && tooltipData && (
            <circle
              cx={tooltipData.x}
              cy={tooltipData.y}
              r={chartTheme.point.radius + 2}
              fill={chartTheme.line.color}
              stroke={chartTheme.background}
              strokeWidth={2}
              style={{ pointerEvents: "none" }}
            />
          )}

          <AxisBottom
            scale={xScale}
            top={innerHeight}
            tickFormat={getTickConfig.tickFormat}
            stroke={chartTheme.axis}
            tickStroke={chartTheme.axis}
            tickLength={6}
            tickValues={getTickConfig.tickValues}
            tickLabelProps={() => ({
              fill: chartTheme.tickLabel,
              fontSize: 11,
              textAnchor: "middle",
              dy: "0.2em",
            })}
          />
        </Group>
      </svg>

      {tooltipOpen && tooltipData && (
        <TooltipInPortal
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
          style={tooltipStyles}
        >
          <div>Value:{formatNumber(tooltipData.point.value || 0)}</div>
          <div>Date:{tooltipData.point.date.toDateString()}</div>
        </TooltipInPortal>
      )}
    </div>
  );
};

export default TimeSeriesChart;
